/**
 * Family-scoped store reset registry.
 *
 * Every feature store that caches ACTIVE-FAMILY-scoped data (tasks, events,
 * albums, notifications, …) registers a reset callback here. Switching the
 * active family (or logging out) calls {@link resetAllFamilyScopedStores} so
 * no data from the previous family scope leaks into the next one.
 *
 * The registry lives in `shared/` so both feature stores and the family
 * feature can use it without cross-feature imports.
 *
 * @module shared/lib/storeReset
 */

type ResetCallback = () => void;

const callbacks = new Set<ResetCallback>();

/**
 * Register a reset callback. Returns an unregister function.
 */
export function registerFamilyScopedReset(callback: ResetCallback): () => void {
  callbacks.add(callback);
  return () => {
    callbacks.delete(callback);
  };
}

/**
 * Reset every registered family-scoped store. Individual failures are
 * isolated so one faulty reset never blocks the others.
 */
export function resetAllFamilyScopedStores(): void {
  for (const callback of callbacks) {
    try {
      callback();
    } catch (error) {
      console.error('Family-scoped store reset failed:', error);
    }
  }
}
